import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BellIcon } from '@phosphor-icons/react';

import { NotificationCard } from '@/components/notifications/NotificationCard';
import { Button } from '@/components/ui/button';
import {
  useInfiniteUserNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMarkNotificationsInboxSeen,
  useNotificationDestinationAvailabilityMap,
  useUnreadNotificationCount,
} from '@/lib/hooks/useUserNotifications';
import { formatDateTimeForList } from '@/lib/utils/date.utils';
import {
  getNotificationRouteTarget,
  getNotificationPillVariant,
  getNotificationTypeLabel,
} from '@/lib/utils/notifications.utils';

export function Notifications() {
  const navigate = useNavigate();
  const hasMarkedInboxSeenRef = useRef(false);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteUserNotifications();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const markAllRead = useMarkAllNotificationsRead();
  const markRead = useMarkNotificationRead();
  const markInboxSeen = useMarkNotificationsInboxSeen();

  const notifications = data?.pages.flatMap((page) => page.data) ?? [];
  const { data: destinationAvailabilityMap = {} } =
    useNotificationDestinationAvailabilityMap(notifications);

  useEffect(() => {
    if (hasMarkedInboxSeenRef.current) {
      return;
    }

    hasMarkedInboxSeenRef.current = true;

    void markInboxSeen.mutateAsync().catch((mutationError) => {
      console.error('Failed to mark inbox seen:', mutationError);
    });
  }, [markInboxSeen]);

  const handleOpenDestination = (
    notification: (typeof notifications)[number]
  ) => {
    void markRead.mutateAsync(notification.id).catch((mutationError) => {
      console.error('Failed to mark notification read:', mutationError);
    });

    const routeTarget = getNotificationRouteTarget(
      notification,
      'notifications'
    );

    if (routeTarget) {
      void navigate(routeTarget);
      return;
    }

    window.location.assign(notification.destination_url);
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 pb-8">
        <div className="space-y-4 animate-pulse">
          <div className="h-8 w-40 rounded bg-slate-700" />
          <div className="h-24 rounded-2xl bg-slate-700" />
          <div className="h-24 rounded-2xl bg-slate-700" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 pb-8">
        <p className="text-body text-red-400">
          Failed to load notifications. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-field-74 text-h1 text-slate-50 mb-2">
            Notifications
          </h1>
          <p className="text-body text-slate-300">
            Catch up on reminders, updates, and community activity.
          </p>
        </div>

        {unreadCount > 0 && (
          <Button
            type="button"
            variant="outline"
            className="self-center hover:text-sky-200 whitespace-normal text-left sm:shrink-0"
            onClick={() => void markAllRead.mutateAsync()}
            disabled={markAllRead.isPending}
          >
            {markAllRead.isPending ? 'Marking…' : 'Mark all as read'}
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="rounded-2xl bg-slate-700 px-6 py-10 text-center">
          <BellIcon
            size={28}
            weight="regular"
            className="mx-auto mb-3 text-slate-400"
          />
          <h2 className="text-large text-slate-50 mb-2">
            No notifications yet
          </h2>
          <p className="text-body text-slate-400">
            When notifications are sent, they&apos;ll show up here for later
            reference.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const isClickable =
              destinationAvailabilityMap[notification.id] ?? false;

            return (
              <NotificationCard
                key={notification.id}
                title={notification.title}
                body={notification.body}
                formattedDate={formatDateTimeForList(notification.created_at)}
                typeLabel={getNotificationTypeLabel(
                  notification.notification_type
                )}
                pillVariant={getNotificationPillVariant(
                  notification.notification_type
                )}
                isRead={!!notification.read_at}
                isClickable={isClickable}
                onOpenDestination={
                  isClickable
                    ? () => handleOpenDestination(notification)
                    : undefined
                }
              />
            );
          })}

          {hasNextPage && (
            <div className="pt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
